import { useCallback, useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { Toaster, toast } from "sonner";
import { authHeaders, getCurrentUser, handleUnauthorized } from "@/lib/auth";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const EMPTY_SUMMARY = {
  total_paid_meta_leads: 0,
  converted_customers: 0,
  conversion_rate: 0,
  current_mrr: 0,
  monthly_revenue_per_customer: 59,
  total_spend: 0,
  cost_per_lead: null,
  cost_per_customer: null,
  roas: null,
  spend_records: [],
  first_touch_campaigns: [],
  last_touch_campaigns: [],
  first_touch_ads: [],
  last_touch_ads: [],
};

function todayIso() {
  return new Date().toISOString().slice(0, 10);
}

function money(value) {
  const number = Number(value || 0);
  return number.toLocaleString(undefined, {
    style: "currency",
    currency: "USD",
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
}

function metricMoney(value) {
  if (value === null || value === undefined) return "—";
  return money(value);
}

function MetricCard({ label, value, note }) {
  return (
    <div className="rounded-2xl border border-white/10 bg-[#121214] p-5">
      <div className="text-[10px] font-bold uppercase tracking-[0.18em] text-zinc-500">
        {label}
      </div>
      <div className="mt-2 font-display text-3xl font-bold text-white">{value}</div>
      <div className="mt-2 text-xs text-zinc-500">{note}</div>
    </div>
  );
}

function BreakdownTable({ title, description, rows = [] }) {
  return (
    <section className="overflow-hidden rounded-2xl border border-white/10 bg-[#121214]">
      <div className="border-b border-white/10 p-5">
        <div className="text-[10px] font-bold uppercase tracking-[0.18em] text-[#00E5FF]">
          {title}
        </div>
        <p className="mt-1 text-sm text-zinc-400">{description}</p>
      </div>

      {rows.length === 0 ? (
        <div className="p-5 text-sm text-zinc-500">
          No paid Meta attribution data yet.
        </div>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full min-w-[560px] text-left text-sm">
            <thead className="bg-black/20 text-[10px] uppercase tracking-[0.14em] text-zinc-500">
              <tr>
                <th className="px-5 py-3">Campaign / Ad</th>
                <th className="px-5 py-3">Leads</th>
                <th className="px-5 py-3">Customers</th>
                <th className="px-5 py-3">MRR</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((row) => (
                <tr
                  key={`${title}-${row.key}`}
                  className="border-t border-white/5"
                >
                  <td className="px-5 py-4 font-medium text-white">{row.key}</td>
                  <td className="px-5 py-4 text-zinc-300">{row.leads ?? 0}</td>
                  <td className="px-5 py-4 text-zinc-300">
                    {row.customers ?? 0}
                  </td>
                  <td className="px-5 py-4 font-semibold text-emerald-300">
                    ${Number(row.mrr || 0).toLocaleString()}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </section>
  );
}

export default function AdminPaidAds() {
  const [currentUser] = useState(() => getCurrentUser());
  const [summary, setSummary] = useState(EMPTY_SUMMARY);
  const [loading, setLoading] = useState(true);
  const [savingSpend, setSavingSpend] = useState(false);
  const [deletingSpendId, setDeletingSpendId] = useState("");

  const [spendForm, setSpendForm] = useState({
    campaign_name: "",
    campaign_id: "",
    ad_id: "",
    amount: "",
    start_date: todayIso(),
    end_date: todayIso(),
    notes: "",
  });

  useEffect(() => {
    if (!currentUser) {
      handleUnauthorized();
      return;
    }

    if (currentUser.role !== "owner") {
      window.location.replace("/staff/mockups");
    }
  }, [currentUser]);

  const loadSummary = useCallback(async () => {
    if (!currentUser || currentUser.role !== "owner") return;

    setLoading(true);

    try {
      const res = await fetch(`${API}/admin/paid-ads/summary`, {
        headers: authHeaders(),
      });

      if (res.status === 401 || res.status === 403) {
        handleUnauthorized();
        return;
      }

      const data = await res.json().catch(() => ({}));

      if (!res.ok) {
        throw new Error(data?.detail || `HTTP ${res.status}`);
      }

      setSummary({
        ...EMPTY_SUMMARY,
        ...data,
        spend_records: Array.isArray(data?.spend_records)
          ? data.spend_records
          : [],
      });
    } catch (err) {
      console.error("Could not load paid ads summary:", err);
      toast.error(err?.message || "Could not load paid ads tracking");
    } finally {
      setLoading(false);
    }
  }, [currentUser]);

  useEffect(() => {
    loadSummary();
  }, [loadSummary]);

  const campaignSuggestions = useMemo(() => {
    const seen = new Set();
    const values = [];

    for (const row of [
      ...(summary.first_touch_campaigns || []),
      ...(summary.last_touch_campaigns || []),
    ]) {
      const key = String(row?.key || "").trim();

      if (!key || key === "(not provided)" || seen.has(key)) {
        continue;
      }

      seen.add(key);
      values.push(key);
    }

    return values;
  }, [summary.first_touch_campaigns, summary.last_touch_campaigns]);

  const updateSpendForm = (field, value) => {
    setSpendForm((current) => ({
      ...current,
      [field]: value,
    }));
  };

  const saveSpend = async (event) => {
    event.preventDefault();

    const amount = Number(spendForm.amount);

    if (!spendForm.campaign_name.trim()) {
      toast.error("Enter the campaign name");
      return;
    }

    if (!Number.isFinite(amount) || amount <= 0) {
      toast.error("Enter a valid spend amount");
      return;
    }

    if (!spendForm.start_date || !spendForm.end_date) {
      toast.error("Choose a start date and end date");
      return;
    }

    setSavingSpend(true);

    try {
      const res = await fetch(`${API}/admin/paid-ads/spend`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          ...authHeaders(),
        },
        body: JSON.stringify({
          campaign_name: spendForm.campaign_name.trim(),
          campaign_id: spendForm.campaign_id.trim(),
          ad_id: spendForm.ad_id.trim(),
          amount,
          start_date: spendForm.start_date,
          end_date: spendForm.end_date,
          notes: spendForm.notes.trim(),
        }),
      });

      if (res.status === 401 || res.status === 403) {
        handleUnauthorized();
        return;
      }

      const data = await res.json().catch(() => ({}));

      if (!res.ok) {
        throw new Error(data?.detail || `HTTP ${res.status}`);
      }

      toast.success("Ad spend saved");

      setSpendForm({
        campaign_name: "",
        campaign_id: "",
        ad_id: "",
        amount: "",
        start_date: todayIso(),
        end_date: todayIso(),
        notes: "",
      });

      await loadSummary();
    } catch (err) {
      console.error("Could not save ad spend:", err);
      toast.error(err?.message || "Could not save ad spend");
    } finally {
      setSavingSpend(false);
    }
  };

  const deleteSpend = async (record) => {
    if (!record?.id) return;

    const confirmed = window.confirm(
      `Delete ${money(record.amount)} of ad spend for "${record.campaign_name}"?`
    );

    if (!confirmed) return;

    setDeletingSpendId(record.id);

    try {
      const res = await fetch(`${API}/admin/paid-ads/spend/${record.id}`, {
        method: "DELETE",
        headers: authHeaders(),
      });

      if (res.status === 401 || res.status === 403) {
        handleUnauthorized();
        return;
      }

      if (!res.ok) {
        const data = await res.json().catch(() => ({}));
        throw new Error(data?.detail || `HTTP ${res.status}`);
      }

      toast.success("Ad spend deleted");
      await loadSummary();
    } catch (err) {
      console.error("Could not delete ad spend:", err);
      toast.error(err?.message || "Could not delete ad spend");
    } finally {
      setDeletingSpendId("");
    }
  };

  if (!currentUser || currentUser.role !== "owner") {
    return null;
  }

  return (
    <div className="min-h-screen bg-[#09090B] text-white font-body">
      <header className="sticky top-0 z-40 border-b border-white/5 bg-[#09090B]/90 backdrop-blur-xl">
        <div className="mx-auto flex h-14 max-w-6xl items-center justify-between px-6 md:px-10">
          <div className="flex items-center gap-2">
            <span className="inline-flex h-6 w-6 items-center justify-center rounded bg-[#00E5FF] text-xs font-bold text-black">
              T
            </span>
            <span className="text-sm font-semibold">
              TapToTable{" "}
              <span className="font-normal text-zinc-500">· admin</span>
            </span>
          </div>

          <Link
            to="/admin/mockups"
            className="rounded-full border border-white/10 bg-white/5 px-4 py-2 text-xs font-semibold text-white hover:bg-white/10"
          >
            ← Restaurant Pages
          </Link>
        </div>
      </header>

      <main className="mx-auto max-w-6xl px-6 py-10 md:px-10">
        <Toaster theme="dark" position="top-center" />

        <div className="mb-8 flex flex-col gap-4 md:flex-row md:items-end md:justify-between">
          <div>
            <div className="text-[10px] font-bold uppercase tracking-[0.2em] text-[#00E5FF]">
              Paid Ads
            </div>
            <h1 className="mt-1 font-display text-3xl font-semibold">
              Meta Paid Ad Tracking
            </h1>
            <p className="mt-2 max-w-2xl text-sm text-zinc-400">
              Track Facebook and Instagram paid leads, ad spend, paying
              customers, acquisition cost, and return on ad spend.
            </p>
          </div>

          <button
            type="button"
            onClick={loadSummary}
            disabled={loading}
            className="rounded-full border border-white/10 bg-white/5 px-4 py-2.5 text-sm font-semibold text-white hover:bg-white/10 disabled:opacity-50"
          >
            {loading ? "Refreshing…" : "Refresh"}
          </button>
        </div>

        {loading ? (
          <div className="rounded-2xl border border-white/10 bg-[#121214] p-8 text-sm text-zinc-500">
            Loading paid ad tracking…
          </div>
        ) : (
          <>
            <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
              <MetricCard
                label="Ad Spend"
                value={money(summary.total_spend)}
                note="Manual Meta spend entered below"
              />
              <MetricCard
                label="Paid Meta Leads"
                value={Number(summary.total_paid_meta_leads || 0).toLocaleString()}
                note="Signup leads attributed to Meta paid traffic"
              />
              <MetricCard
                label="Cost Per Lead"
                value={metricMoney(summary.cost_per_lead)}
                note="Ad spend divided by paid Meta leads"
              />
              <MetricCard
                label="Paying Customers"
                value={Number(summary.converted_customers || 0).toLocaleString()}
                note="Paid + active TapToTable accounts"
              />
              <MetricCard
                label="CAC"
                value={metricMoney(summary.cost_per_customer)}
                note="Ad spend divided by paying customers"
              />
              <MetricCard
                label="Conversion Rate"
                value={`${Number(summary.conversion_rate || 0).toFixed(2)}%`}
                note="Paid Meta leads that became customers"
              />
              <MetricCard
                label="Current MRR"
                value={money(summary.current_mrr)}
                note={`Calculated at $${Number(
                  summary.monthly_revenue_per_customer || 59
                )}/month per active customer`}
              />
              <MetricCard
                label="ROAS"
                value={
                  summary.roas === null || summary.roas === undefined
                    ? "—"
                    : `${Number(summary.roas).toFixed(2)}x`
                }
                note="Current attributed MRR divided by ad spend"
              />
            </div>

            <section className="mt-8 rounded-2xl border border-white/10 bg-[#121214]">
              <div className="border-b border-white/10 p-5">
                <div className="text-[10px] font-bold uppercase tracking-[0.18em] text-emerald-300">
                  Add Meta Ad Spend
                </div>
                <h2 className="mt-1 font-display text-xl font-semibold">
                  Record campaign spend
                </h2>
                <p className="mt-1 text-sm text-zinc-400">
                  Enter the amount reported by Meta Ads Manager for the matching
                  campaign and date range.
                </p>
              </div>

              <form onSubmit={saveSpend} className="grid gap-4 p-5 md:grid-cols-2">
                <div className="md:col-span-2">
                  <label className="mb-2 block text-xs font-semibold text-zinc-300">
                    Campaign Name *
                  </label>
                  <input
                    list="paid-ad-campaigns"
                    value={spendForm.campaign_name}
                    onChange={(event) =>
                      updateSpendForm("campaign_name", event.target.value)
                    }
                    placeholder="restaurant_owner_test"
                    className="w-full rounded-xl border border-white/10 bg-black/20 px-4 py-3 text-sm text-white placeholder-zinc-600 focus:border-cyan-400 focus:outline-none"
                  />
                  <datalist id="paid-ad-campaigns">
                    {campaignSuggestions.map((campaign) => (
                      <option key={campaign} value={campaign} />
                    ))}
                  </datalist>
                  <p className="mt-2 text-xs text-zinc-500">
                    Use the exact UTM campaign name whenever possible.
                  </p>
                </div>

                <div>
                  <label className="mb-2 block text-xs font-semibold text-zinc-300">
                    Campaign ID
                  </label>
                  <input
                    value={spendForm.campaign_id}
                    onChange={(event) =>
                      updateSpendForm("campaign_id", event.target.value)
                    }
                    placeholder="Optional Meta campaign ID"
                    className="w-full rounded-xl border border-white/10 bg-black/20 px-4 py-3 text-sm text-white placeholder-zinc-600 focus:border-cyan-400 focus:outline-none"
                  />
                </div>

                <div>
                  <label className="mb-2 block text-xs font-semibold text-zinc-300">
                    Ad ID
                  </label>
                  <input
                    value={spendForm.ad_id}
                    onChange={(event) =>
                      updateSpendForm("ad_id", event.target.value)
                    }
                    placeholder="Optional Meta ad ID"
                    className="w-full rounded-xl border border-white/10 bg-black/20 px-4 py-3 text-sm text-white placeholder-zinc-600 focus:border-cyan-400 focus:outline-none"
                  />
                </div>

                <div>
                  <label className="mb-2 block text-xs font-semibold text-zinc-300">
                    Spend Amount *
                  </label>
                  <div className="relative">
                    <span className="pointer-events-none absolute left-4 top-1/2 -translate-y-1/2 text-zinc-500">
                      $
                    </span>
                    <input
                      type="number"
                      min="0.01"
                      step="0.01"
                      value={spendForm.amount}
                      onChange={(event) =>
                        updateSpendForm("amount", event.target.value)
                      }
                      placeholder="15.00"
                      className="w-full rounded-xl border border-white/10 bg-black/20 py-3 pl-8 pr-4 text-sm text-white placeholder-zinc-600 focus:border-cyan-400 focus:outline-none"
                    />
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="mb-2 block text-xs font-semibold text-zinc-300">
                      Start Date *
                    </label>
                    <input
                      type="date"
                      value={spendForm.start_date}
                      onChange={(event) =>
                        updateSpendForm("start_date", event.target.value)
                      }
                      className="w-full rounded-xl border border-white/10 bg-[#1A1A1F] px-3 py-3 text-sm text-white focus:border-cyan-400 focus:outline-none"
                    />
                  </div>

                  <div>
                    <label className="mb-2 block text-xs font-semibold text-zinc-300">
                      End Date *
                    </label>
                    <input
                      type="date"
                      value={spendForm.end_date}
                      onChange={(event) =>
                        updateSpendForm("end_date", event.target.value)
                      }
                      className="w-full rounded-xl border border-white/10 bg-[#1A1A1F] px-3 py-3 text-sm text-white focus:border-cyan-400 focus:outline-none"
                    />
                  </div>
                </div>

                <div className="md:col-span-2">
                  <label className="mb-2 block text-xs font-semibold text-zinc-300">
                    Notes
                  </label>
                  <textarea
                    rows={3}
                    value={spendForm.notes}
                    onChange={(event) =>
                      updateSpendForm("notes", event.target.value)
                    }
                    placeholder="Optional notes about this spend period"
                    className="w-full resize-y rounded-xl border border-white/10 bg-black/20 px-4 py-3 text-sm text-white placeholder-zinc-600 focus:border-cyan-400 focus:outline-none"
                  />
                </div>

                <div className="md:col-span-2">
                  <button
                    type="submit"
                    disabled={savingSpend}
                    className="inline-flex items-center justify-center rounded-full bg-[#00E5FF] px-5 py-2.5 text-sm font-bold text-black transition-colors hover:bg-[#66FFFF] disabled:cursor-not-allowed disabled:opacity-50"
                  >
                    {savingSpend ? "Saving Spend…" : "Save Ad Spend"}
                  </button>
                </div>
              </form>
            </section>

            <section className="mt-8 overflow-hidden rounded-2xl border border-white/10 bg-[#121214]">
              <div className="border-b border-white/10 p-5">
                <div className="text-[10px] font-bold uppercase tracking-[0.18em] text-emerald-300">
                  Spend History
                </div>
                <p className="mt-1 text-sm text-zinc-400">
                  Manual Meta spend records currently included in your totals.
                </p>
              </div>

              {(summary.spend_records || []).length === 0 ? (
                <div className="p-5 text-sm text-zinc-500">
                  No ad spend entered yet.
                </div>
              ) : (
                <div className="overflow-x-auto">
                  <table className="w-full min-w-[900px] text-left text-sm">
                    <thead className="bg-black/20 text-[10px] uppercase tracking-[0.14em] text-zinc-500">
                      <tr>
                        <th className="px-5 py-3">Campaign</th>
                        <th className="px-5 py-3">Spend</th>
                        <th className="px-5 py-3">Date Range</th>
                        <th className="px-5 py-3">Campaign ID</th>
                        <th className="px-5 py-3">Ad ID</th>
                        <th className="px-5 py-3">Notes</th>
                        <th className="px-5 py-3"></th>
                      </tr>
                    </thead>

                    <tbody>
                      {summary.spend_records.map((record) => (
                        <tr key={record.id} className="border-t border-white/5">
                          <td className="px-5 py-4 font-medium text-white">
                            {record.campaign_name}
                          </td>
                          <td className="px-5 py-4 font-semibold text-emerald-300">
                            {money(record.amount)}
                          </td>
                          <td className="px-5 py-4 text-zinc-300">
                            {record.start_date} → {record.end_date}
                          </td>
                          <td className="px-5 py-4 text-zinc-400">
                            {record.campaign_id || "—"}
                          </td>
                          <td className="px-5 py-4 text-zinc-400">
                            {record.ad_id || "—"}
                          </td>
                          <td className="max-w-[260px] px-5 py-4 text-zinc-400">
                            <span className="line-clamp-2">
                              {record.notes || "—"}
                            </span>
                          </td>
                          <td className="px-5 py-4 text-right">
                            <button
                              type="button"
                              onClick={() => deleteSpend(record)}
                              disabled={deletingSpendId === record.id}
                              className="rounded-full border border-red-500/30 bg-red-500/10 px-3 py-1.5 text-xs font-semibold text-red-300 hover:bg-red-500/20 disabled:opacity-50"
                            >
                              {deletingSpendId === record.id
                                ? "Deleting…"
                                : "Delete"}
                            </button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </section>

            <div className="mt-8 grid gap-6 lg:grid-cols-2">
              <BreakdownTable
                title="First-Touch Campaigns"
                description="Which Meta campaign first introduced each paid lead."
                rows={summary.first_touch_campaigns}
              />
              <BreakdownTable
                title="Last-Touch Campaigns"
                description="Which Meta campaign was the most recent tracked touch before signup."
                rows={summary.last_touch_campaigns}
              />
              <BreakdownTable
                title="First-Touch Ads"
                description="Lead and customer results grouped by first-touch Meta ad ID."
                rows={summary.first_touch_ads}
              />
              <BreakdownTable
                title="Last-Touch Ads"
                description="Lead and customer results grouped by last-touch Meta ad ID."
                rows={summary.last_touch_ads}
              />
            </div>
          </>
        )}
      </main>
    </div>
  );
}
